import * as _ from 'lodash';
import {
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { REPORTS_V2 } from '../../../../shared/featureFlags/flagNames';
import { useParams, useQuery } from '../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../helpers/features';
import ProjectGroupsModule from '../../../redux/project_grouping';
import { nuc } from '../../../utils';
import { useReportOptionsParams } from '../hooks/useReportOptionsParams';
import { isReleaseGroupScope, type ReportsV3Params, type ReportsV3QueryParams, type ReportV3Format } from '../types';
import type { BaseFossaError } from '../../../types';

const DEBOUNCE_TIMEOUT = 1000;

const getURLParseBase = (): string => {
  if (typeof window !== 'undefined' && window.location.origin) {
    return window.location.origin;
  }

  return 'http://localhost';
};

/**
 * Returns a canonical URL string with query params sorted by key/value,
 * so semantically equivalent URLs compare equal even if parameter order differs.
 */
const normalizePreviewURL = (url: string): string => {
  try {
    const parsedURL = new URL(url, getURLParseBase());
    const sortedParams = [...parsedURL.searchParams.entries()].sort(([keyA, valueA], [keyB, valueB]) => {
      if (keyA === keyB) {
        return valueA.localeCompare(valueB);
      }
      return keyA.localeCompare(keyB);
    });
    const normalizedQuery = new URLSearchParams(sortedParams).toString();
    return `${parsedURL.pathname}${normalizedQuery ? `?${normalizedQuery}` : ''}`;
  } catch {
    return url;
  }
};

const getPreviewFormat = (url: string): string | null => {
  try {
    return new URL(url, getURLParseBase()).searchParams.get('format');
  } catch {
    return null;
  }
};

type LoadPreview = {
  /**
   * URL to use as the iframe `key`. This forces the iframe to be reloaded when the URL changes.
   */
  cacheBustedURL: string;
  /**
   * The debounced preview URL. Use this as the iframe `src` so the iframe
   * doesn't navigate on every rapid option change.
   */
  debouncedPreviewURL: string;
  /**
   * Whether the iframe is currently loading content from the server.
   */
  isLoading: boolean;
  /**
   * Whether the preview URL has changed but the debounce hasn't settled yet.
   * Currently hides the iframe alongside `isLoading`, but separated to allow
   * distinct visual treatment in the future (e.g. a subtle loading indicator).
   */
  isPending: boolean;
  /**
   * Error message from the iframe, if any could be parsed based on the content.
   */
  iframeError: string | null;
  /**
   * Callback to be called when the iframe loads.
   */
  onIframeLoad: (event: SyntheticEvent<HTMLIFrameElement>) => void;
};

/**
 * A hook for managing the loading state of the preview iframe.
 * Debounces incoming URL changes (1s) so that rapid option toggling doesn't
 * hammer the backend. Format changes and explicit bypasses skip the debounce.
 * Resets to "loading" whenever the debounced URL settles.
 * Also manages cache busting in the preview URL, which we need to ensure that
 * when the preview URL changes, the iframe is actually reloaded.
 *
 * @param {string} previewURL - The URL of the preview iframe.
 * @param {boolean} [shouldBypassDebounce] - Whether this update should skip debounce.
 * @param {number} [savedOptionsBypassRequestId] - Monotonic request id for saved-options-triggered bypasses.
 */
export const useLoadPreview = (
  previewURL: string,
  shouldBypassDebounce = false,
  savedOptionsBypassRequestId = 0,
): LoadPreview => {
  const [debouncedURL, setDebouncedURL] = useState(previewURL);
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const normalizedPreviewURL = useMemo(() => normalizePreviewURL(previewURL), [previewURL]);
  const normalizedDebouncedURL = useMemo(() => normalizePreviewURL(debouncedURL), [debouncedURL]);
  const lastHandledSavedOptionsBypassRequestIdRef = useRef(savedOptionsBypassRequestId);

  // This is how we determine that the iframe has actually loaded.
  // It also attempts to handle errors from the iframe, which are difficult to
  // determine since React doesn't provide a good way to catch things like 400 errors
  // or 500 errors from the iframe. This is fairly brittle, but in most cases improves the UX greatly.
  const handleIframeLoad = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
    setIframeLoaded(true);
    setIframeError(null);

    try {
      const iframe = event.currentTarget;
      // This only works because this is the same origin.
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      const content = doc?.body?.innerHTML.trim() ?? '';

      // Handle empty iframe errors.
      if (!doc || content === '') {
        setIframeError('The preview is empty');
        return;
      }

      // Error responses from the server are plain JSON wrapped in a single <pre> element,
      // so <pre> is the very first thing in the body. We also check for the `httpStatusCode`
      // substring as a cheap guard to avoid false-positives on legitimate report content
      // that happens to contain <pre> blocks (code snippets, license text, etc.).
      if (content.startsWith('<pre') && content.includes('httpStatusCode')) {
        const preElement = doc.querySelector('pre');
        const textContent = preElement?.textContent ?? '';
        try {
          const json = JSON.parse(textContent) as BaseFossaError;
          if (json.httpStatusCode && json.httpStatusCode >= 400 && json.httpStatusCode < 600) {
            setIframeError(json.message);
          }
        } catch {
          setIframeError('An error occurred while generating the preview');
        }
      }
    } catch {
      // Cross-origin iframes (e.g. PDF previews) throw a DOMException when
      // accessing contentDocument/contentWindow. The iframe has still loaded
      // successfully, so we silently swallow the error.
    }
  }, []);

  const debouncedSetURL = useMemo(() => _.debounce(setDebouncedURL, DEBOUNCE_TIMEOUT), []);

  useEffect(() => {
    const hasPendingSavedOptionsBypass =
      savedOptionsBypassRequestId > lastHandledSavedOptionsBypassRequestIdRef.current;

    if (normalizedPreviewURL === normalizedDebouncedURL) {
      debouncedSetURL.cancel();
      return () => debouncedSetURL.cancel();
    }

    const shouldBypassDebounceForFormat = getPreviewFormat(previewURL) !== getPreviewFormat(debouncedURL);

    if (shouldBypassDebounce || shouldBypassDebounceForFormat || hasPendingSavedOptionsBypass) {
      debouncedSetURL.cancel();
      setDebouncedURL(previewURL);
      if (hasPendingSavedOptionsBypass) {
        lastHandledSavedOptionsBypassRequestIdRef.current = savedOptionsBypassRequestId;
      }
      return () => debouncedSetURL.cancel();
    }

    debouncedSetURL(previewURL);
    return () => debouncedSetURL.cancel();
  }, [
    previewURL,
    debouncedURL,
    normalizedPreviewURL,
    normalizedDebouncedURL,
    shouldBypassDebounce,
    savedOptionsBypassRequestId,
    debouncedSetURL,
  ]);

  useEffect(() => {
    setIframeLoaded(false);
    setIframeError(null);
  }, [debouncedURL]);

  // Date.now() is intentionally impure here — we want a unique cache-buster
  // each time debouncedURL changes to force the iframe to reload.
  const cacheBustedURL = useMemo(() => {
    return `${debouncedURL}${debouncedURL.includes('?') ? '&' : '?'}_cb=${Date.now()}`;
  }, [debouncedURL]);

  return {
    cacheBustedURL,
    debouncedPreviewURL: debouncedURL,
    isLoading: !isIframeLoaded,
    isPending: normalizedPreviewURL !== normalizedDebouncedURL,
    iframeError,
    onIframeLoad: handleIframeLoad,
  };
};

type ProjectReportURL = `/api${'/v2' | ''}/revisions/${string}/attribution/preview?preview=true${string}`;
type ReleaseGroupReportURL =
  `/api${'/v2' | ''}/project_group/${number}/release/${number}/attribution/${ReportV3Format}?preview=true${string}`;

/**
 * Builds the preview URL from route params, the selected report format,
 * feature flags, and report option params. Supports both project-scope
 * and release-group-scope URLs.
 */
export const usePreviewURL = (): ProjectReportURL | ReleaseGroupReportURL => {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  // Note: this will be removed at some point, maybe sooner than later,
  // because this page will eventually ONLY support v2. But in the meantime,
  // we want to test v1 and v2 in parallel until all the v2 reports are live.
  const isReportsV2Enabled = useIsFeatureEnabled(REPORTS_V2);
  const apiVersion = isReportsV2Enabled ? '/v2' : '';
  const [query] = useQuery<ReportsV3QueryParams>();
  const reportFormat = query.format ?? 'HTML';
  // The most reliable way to get the release ID is to use the selected release ID from Redux.
  // Ideally this would be like a query param but oh well
  const selectedReleaseId = useSelector(ProjectGroupsModule.selectors.getSelectedReleaseId);
  const reportOptionsParams = useReportOptionsParams();

  return useMemo<ProjectReportURL | ReleaseGroupReportURL>(() => {
    if (isReleaseGroup) {
      // Note that releaseId as a URL param part is not likely to actually exist for this path.
      // We'll try it (since we should really refactor Release Groups to be nested like that), but if it
      // doesn't exist, we'll use the selected release ID from Redux.

      //  Note that format is both a URL param and a query param.
      const { projectGroupId, releaseId } = params;
      return `/api${apiVersion}/project_group/${projectGroupId}/release/${releaseId ?? selectedReleaseId ?? 0}/attribution/${reportFormat}?preview=true&format=${reportFormat}${reportOptionsParams ? `&${reportOptionsParams}` : ''}`;
    }

    const projectLocator = nuc(`${params.project_id}$${params.revision_id}`);
    return `/api${apiVersion}/revisions/${projectLocator}/attribution/preview?preview=true&format=${reportFormat}${reportOptionsParams ? `&${reportOptionsParams}` : ''}`;
  }, [apiVersion, isReleaseGroup, params, reportFormat, reportOptionsParams, selectedReleaseId]);
};
